import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreatorProfile } from "models";
import type { CommentInput as CommentInputType } from "models";
import {
  AppRoutes,
  Avatar,
  Box,
  Button,
  Colors,
  LOCAL_STORAGE_KEYS,
  Link,
  RoleDisplayer,
  TextVariants,
  Text,
  PremiumBadge,
  SummaryTileStar,
  ShareProfile,
  Chip,
  Menu,
  Dropdown,
} from "reusable";
import { useUser, useLocalStorage, useAlerts, useComments } from "../../../backend";
import PhotoSection from "./PhotosSection";
import { GetInTouch } from "../../../reusable/components/get-in-touch";
import { CommentCarousel, CommentModal, CommentInput } from "./comments";

interface ProfileDetail {
  label: string;
  value: string | string[];
}

interface ProfileStat {
  label: string;
  value: string | number;
}

interface ProfileLink {
  label: string;
  value: string;
}

const ProfileTile = (props: {
  profile: CreatorProfile;
  isPreview?: boolean;
}): JSX.Element => {
  const { profile, isPreview } = props;
  const goTo = useNavigate();
  const { loggedInUser } = useUser();
  const { isMobileScreen } = useLocalStorage();
  const commentsEnabled = false;
  const [mobileTab, setMobileTab] = useState<'gallery' | 'about' | 'comments'>(isMobileScreen ? 'gallery' : 'about');
  const { addAlert } = useAlerts();
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);

  const {
    companyName,
    completedProjects,
    experienceYears,
    projectCost,
    projectType,
    languages,
    about,
    websiteUrl,
    instagramLink,
    profilePicture = "",
    occupation,
    name,
    location,
    isSubscribed = false,
    ratings,
    userId,
    position,
    graduationYear,
    softwareSkills,
    theme,
    universityName,
    contactNumber,
  } = profile;

  // Normalize profilePicture to always be a string
  const normalizedProfilePicture = Array.isArray(profilePicture) 
    ? profilePicture[0] || "" 
    : profilePicture || "";

  const isOwner = position === "Business Owner";
  const isStudent = position === "Student";
  const canEdit = loggedInUser?.userId === profile?.userId;
  const profileId = commentsEnabled ? (userId || "") : "";
  const isUserLoggedIn = Boolean(loggedInUser?.userId);

  const {
    comments,
    loading: commentsLoading,
    error: commentsError,
    lastUpdate,
    currentPage,
    totalPages,
    hasMore,
    totalCount,
    addComment,
    refreshComments,
    nextPage,
    prevPage,
    goToPage,
  } = useComments(profileId);

  const profileStats: ProfileStat[] = isOwner
    ? [
        {
          label: "Projects",
          value: completedProjects || 0,
        },
        {
          label: "Avg. Cost",
          value:
            typeof projectCost === "string" &&
            (projectCost.toLowerCase().includes("lakh") ||
              projectCost === "-" ||
              projectCost.toLowerCase().includes("lac"))
              ? projectCost
              : `${projectCost || 0} Lakhs`,
        },
        {
          label: "Years Exp.",
          value: experienceYears || 0,
        },
      ]
    : [];

  const profileDetails: ProfileDetail[] = isOwner
    ? [
        { label: "About", value: about || "" },
        { label: "Registered Company", value: companyName || "" },
        { label: "Project Type", value: projectType || "" },
        { label: "Languages", value: languages ? languages.split(", ").filter(lang => lang.trim()) : [] },
      ]
    : [
        { label: "About", value: about || "" },
        { label: "Work Experience", value: String(experienceYears || "") },
        {
          label: isStudent
            ? "University/Institution"
            : "Current/Previous Company",
          value: isStudent ? (universityName || "") : (companyName || ""),
        },
        {
          label: "Graduation Year",
          value: String(graduationYear || ""),
        },
        { label: "Languages", value: languages ? languages.split(", ").filter(lang => lang.trim()) : [] },
        { label: "Software Skills", value: softwareSkills ? softwareSkills.split(", ").filter(skill => skill.trim()) : [] },
      ];

  const profileLinks: ProfileLink[] = [
    { label: "Website", value: websiteUrl || "" },
    { label: "Instagram", value: instagramLink || "" },
  ];

  useEffect(() => {
    // Initialize mobile tab when screen size changes
    if (isMobileScreen) {
      setMobileTab('gallery');
    } else {
      setMobileTab('about');
    }
  }, [isMobileScreen]);

  const handleSubmitComment = async (
    comment: CommentInputType,
    guestData?: { name: string; email: string }
  ) => {
    if (!profileId) return;

    const commentPayload = { ...comment, profileId };
    if (isUserLoggedIn && loggedInUser?.userId) {
      const userName = loggedInUser?.name || "User";
      const userProfilePicture = loggedInUser?.profilePicture || loggedInUser?.profilePhotoUrl || "";
      await addComment(commentPayload, loggedInUser.userId, userName, userProfilePicture);
      return;
    }

    if (!guestData) {
      throw new Error("Guest details are required to comment.");
    }

    const guestId = `guest-${Date.now()}`;
    await addComment(commentPayload, guestId, guestData.name, "", guestData.email);
  };

  const handleModalSubmit = async (data: {
    name: string;
    phone: string;
    email: string;
    message: string;
  }) => {
    if (!profileId) return;

    const guestId = `guest-${Date.now()}`;
    await addComment(
      { profileId, content: data.message },
      guestId,
      data.name,
      "",
      data.email,
      data.phone
    );
  };


  const { overall, service, design, delivery, totalRatings } = ratings || { overall: 0, service: 0, design: 0, delivery: 0, totalRatings: 0 };

  const userRatings = [
    { label: "Overall", value: overall },
    { label: "Service", value: service },
    { label: "Design", value: design },
    { label: "Delivery", value: delivery },
  ];

  const getProfileUrl = () => {
    const { protocol, host, pathname } = window.location;
    return `${protocol}//${host}${pathname}`;
  };

  const handleEditOrFeedback = () => {
    if (canEdit) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.STEP_IN_PROGRESS, "3");
      goTo(AppRoutes.FILL_PROFILE_DETAILS.replace(":userId", userId ));
    } else {
      goTo(AppRoutes.FEEDBACK.replace(":userId", userId ));
    }
  };

  const renderProfileButtons = () => {
    if (isPreview) return null;

    const hasRatings = totalRatings && totalRatings > 0;
    const isProfileOwner = canEdit;
    const isVisitor = !isProfileOwner;

    if (isProfileOwner) {
      // Dropdown options for owner: Edit, Manage Account, Logout
      const dropdownOptions = [
        {
          title: 'Edit',
          onClick: () => {
            localStorage.setItem(LOCAL_STORAGE_KEYS.STEP_IN_PROGRESS, "3");
            goTo(AppRoutes.FILL_PROFILE_DETAILS.replace(":userId", userId ));
          },
        },
        ...(commentsEnabled
          ? [
              {
                title: "Write Comment",
                onClick: () => setIsCommentModalOpen(true),
              },
            ]
          : []),
    // {
    //   title: 'Manage Account',
    //   onClick: () => goTo(AppRoutes.MANAGE_ACCOUNT.replace(":userId", userId || dummyUserId)),
    // },
        {
          title: 'Logout',
          onClick: () => {
            localStorage.clear();
            window.location.href = '/';
          },
        },
      ];
      return (
        <Box className="profile-tile-buttons">
          <Button
            className={
              (hasRatings && isOwner)
                ? "profile-tile-button-1"
                : "profile-tile-button-1 profile-tile-get-rating"
            }
            disabled={isPreview}
            onClick={() => {
              if (isOwner) {
                // Business owners always go to edit (regardless of rating count)
                localStorage.setItem(LOCAL_STORAGE_KEYS.STEP_IN_PROGRESS, "3");
                goTo(AppRoutes.FILL_PROFILE_DETAILS.replace(":userId", userId ));
              } else {
                // Employees/Students always go to get verified rating
                goTo(AppRoutes.GET_RATING.replace(":userId", userId ));
              }
            }}
          >
            {isOwner ? 'Edit' : 'Get Verified Rating'}
          </Button>
          <ShareProfile
            profileUrl={getProfileUrl()}
            isPreview={Boolean(isPreview)}
            userName={name}
            title="Share"
          />
          <Dropdown title={<Menu />} options={dropdownOptions} />
        </Box>
      );
    }

    // Visitor/other user logic
    let dropdownOptions: Array<{ title: string; onClick: () => void }> = [];
    const shouldShowGiveFeedback = hasRatings || isOwner; // Business owners always show, others show if they have ratings
    
    if (shouldShowGiveFeedback && isVisitor) {
      const shareMessage = `Discover the amazing work of ${name}, a talented interior designer/architect! Check out their stunning portfolio and innovative designs on kydoscope!`;
      const profileUrl = getProfileUrl();
      dropdownOptions = [
        ...(commentsEnabled
          ? [
              {
                title: "Write Comment",
                onClick: () => setIsCommentModalOpen(true),
              },
            ]
          : []),
        {
          title: "Copy URL",
          onClick: () => {
            navigator.clipboard.writeText(profileUrl);
            addAlert({ type: "success", message: "Link copied to clipboard." });
          },
        },
        {
          title: "WhatsApp",
          onClick: () => {
            const url = encodeURIComponent(profileUrl);
            const text = encodeURIComponent(shareMessage);
            window.open(`https://api.whatsapp.com/send?text=${text}%20${url}`, "_blank");
          },
        },
        {
          title: "Facebook",
          onClick: () => {
            const url = encodeURIComponent(profileUrl);
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank");
          },
        },
      ];
    }
    return (
      <Box className="profile-tile-buttons">
        {shouldShowGiveFeedback ? (
          <>
            <Button
              className="profile-tile-button-1"
              onClick={handleEditOrFeedback}
            >
              Give Feedback
            </Button>
            <GetInTouch
              profileOwnerId={userId || ''}
              visitorId={loggedInUser?.userId || ''}
              isPremium={isSubscribed}
              profilePicture={normalizedProfilePicture}
              ownerName={name}
            />
            <Dropdown title={<Menu />} options={dropdownOptions} />
          </>
        ) : (
          <>
            {commentsEnabled && isVisitor && (
              <Button
                className="profile-tile-button-1"
                onClick={() => setIsCommentModalOpen(true)}
              >
                Write a Comment
              </Button>
            )}
            <ShareProfile
              profileUrl={getProfileUrl()}
              isPreview={Boolean(isPreview)}
              userName={name}
              title="Share"
            />
            <GetInTouch
              profileOwnerId={userId || ''}
              visitorId={loggedInUser?.userId || ''}
              isPremium={isSubscribed}
              profilePicture={normalizedProfilePicture}
              ownerName={name}
            />
          </>
        )}
      </Box>
    );
  };

  const tags = [occupation?.split(", ")[0] || occupation, position];

  return (
    <Box className="profile-tile-container">
      <Box className="profile-tile-1">
        <Box
          className="profile-tile-summary-role"
          background={isSubscribed ? theme?.background || "" : ""}
        >
          <Box padding="43px 0 0 29px">
            <Avatar
              url={normalizedProfilePicture}
              componentKey={String(normalizedProfilePicture)}
              expand={isMobileScreen}
              name={name}
            />
          </Box>

          <Box padding="11px 20px 6px 10px" textAlign="right">
            <RoleDisplayer
              name={tags[0]}
              theme={isSubscribed ? theme?.color ?? "red-displayer" : "red-displayer"}
              color={isSubscribed ? theme?.color : undefined}
            />
            <Box marginTop="23px">
              <Box className="position-at">
                {position === "Employee" ? "Works at " : position + " at "}
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                {/* eslint-disable-next-line */}
                <span style={{ fontWeight: "600" }}> {/* eslint-disable-line */}
                  {" "}
                  {companyName || universityName}
                </span>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className="profile-tile-details">
          <Box className="name-details" borderBottom="1px #e5e7eb solid">
            <Box display="flex" alignItems="center" marginBottom="5px">
              <Text
                color={Colors.BLACK_MAIN}
                variant={TextVariants.DOUBLE_LARGE_TERNARY_FONT_BOLD}
                ellipsis
              >
                {name ? name.charAt(0).toUpperCase() + name.slice(1) : ""}
              </Text>
              {isSubscribed && (
                <span className="premium-badge">
                  <PremiumBadge />
                </span>
              )}
            </Box>
            <Text
              color={Colors.GRAY_4}
              variant={TextVariants.MEDIUM_PRIMARY_FONT}
              style={{ display: "block" }}
            >
              {location?.place}
            </Text>
          </Box>
          <Box className="rating-details">
            <Box className="rating-details-header">
              <Box marginRight="8px" display="inline-block">
                <SummaryTileStar />
              </Box>
              <Text
                variant={TextVariants.LARGE_TERNARY_FONT_BOLD_600}
                color={Colors.BLACK_DARK}
              >
                {isOwner
                  ? "Rating"
                  : position === "Student"
                  ? "Professor Rating"
                  : "Employer Rating"}
              </Text>
              <Text
                color={Colors.GRAY_4}
                variant={TextVariants.MEDIUM_PRIMARY_FONT}
                marginLeft="4px"
              >
                {totalRatings ? `(${totalRatings})` : "No Ratings Yet"}
              </Text>
            </Box>
            <Box className="profile-stats">
              {userRatings.map((rating) => (
                <Box
                  key={rating.label}
                  className="stat-box"
                  display="inline-block"
                >
                  <Box className="state-box-value">
                    {rating.value === 0
                      ? "-"
                      : Math.round(rating.value * 10) / 10}
                    {rating.value !== 0 && (
                      <Text
                        color={Colors.GRAY_5}
                        variant={TextVariants.MEDIUM_PRIMARY_FONT_BOLD}
                        style={{ marginLeft: "1.5px" }}
                      >
                        /5
                      </Text>
                    )}
                  </Box>
                  <Box className="state-box-label">{rating.label}</Box>
                </Box>
              ))}
            </Box>
          </Box>
          {renderProfileButtons()}
        </Box>
      </Box>
      {commentsEnabled && !isMobileScreen && comments.length > 0 && (
        <Box className="profile-comments-box">
          <Box padding="0 0 16px 0">
            {isUserLoggedIn && !canEdit && (
              <CommentInput
                onSubmit={handleSubmitComment}
                loading={commentsLoading}
                isLoggedIn={isUserLoggedIn}
              />
            )}
            {commentsError && (
              <Box marginBottom="8px" color={Colors.ORANGE}>
                {commentsError}
              </Box>
            )}
            <CommentCarousel
              comments={comments}
              loading={commentsLoading}
              lastUpdate={lastUpdate}
              onRefresh={refreshComments}
              currentPage={currentPage}
              totalPages={totalPages}
              hasMore={hasMore}
              totalCount={totalCount}
              onNextPage={nextPage}
              onPrevPage={prevPage}
              onGoToPage={goToPage}
            />
          </Box>
        </Box>
      )}
      <Box className="profile-tile-2">
        <Box className="profile-buttons show-for-small-only">
          <Button
            className={`profile-button ${
              mobileTab === 'gallery' ? "profile-button-selected" : ""
            }`}
            onClick={() => setMobileTab('gallery')}
          >
            Work Gallery
          </Button>
          <Button
            className={`profile-button ${
              mobileTab === 'about' ? "profile-button-selected" : ""
            }`}
            onClick={() => setMobileTab('about')}
          >
            About
          </Button>
          {commentsEnabled && (
            <Button
              className={`profile-button ${
                mobileTab === 'comments' ? "profile-button-selected" : ""
              }`}
              onClick={() => setMobileTab('comments')}
            >
              Comments
            </Button>
          )}
        </Box>
        {mobileTab === 'gallery' && (
          <Box className="show-for-small-only" padding="0 10px">
            <PhotoSection urls={profile?.creatorPhotos} />
          </Box>
        )}
        {mobileTab === 'about' && (
          <Box padding="0 32px">
            <Box className="profile-stats">
              {profileStats.map((stat) => (
                <Box key={stat.label} className="stat-box" display="inline-block">
                  <Box className="state-box-value">{stat.value || 0}</Box>
                  <Box className="state-box-label">{stat.label}</Box>
                </Box>
              ))}
            </Box>
            <Box className="profile-details">
              {profileDetails.map((detail) => (
                <Box key={detail.label} className="profile-detail">
                  <Box className="profile-detail-label">{detail.label}</Box>
                  <Box className="profile-detail-value">
                    {detail.label === "Languages"
                      ? Array.isArray(detail.value)
                        ? (detail.value as string[]).map((v: string, index: number) =>
                            v.length > 1 ? (
                              <Chip
                                key={`${detail.label}-${v}-${index}`}
                                title={v}
                                marginRight="12px"
                                marginBottom="10px"
                              />
                            ) : null
                          )
                        : typeof detail.value === "string"
                        ? (detail.value as string)
                        : "N/A"
                      : detail.label === "Software Skills"
                      ? Array.isArray(detail.value)
                        ? (detail.value as string[]).map((v: string, index: number) =>
                            v.length > 1 ? (
                              <Chip
                                key={`${detail.label}-${v}-${index}`}
                                title={v}
                                marginRight="12px"
                                marginBottom="10px"
                              />
                            ) : null
                          )
                        : typeof detail.value === "string"
                        ? (detail.value as string)
                        : "N/A"
                      : detail.value || "N/A"}
                  </Box>
                </Box>
              ))}
            </Box>
            {isSubscribed && (
              <Box>
                <Box className="profile-detail-label">Contact Number</Box>{" "}
                <Box className="profile-detail-value">{contactNumber}</Box>
              </Box>
            )}
            {isSubscribed && (websiteUrl || instagramLink) && (
              <Box className="profile-links-section">
                <Box className="profile-detail-label">Links</Box>
                {profileLinks.map((link: any) =>
                  link?.value && link?.value !== "-" ? (
                    <Box
                      display="inline-block"
                      marginRight="10px"
                      key={link.label}
                    >
                      <Link
                        onClick={() =>
                          window.open(
                            `${
                              link?.value.includes("http")
                                ? link.value
                                : `https://${link?.value}`
                            }`,
                            "_blank"
                          )
                        }
                      >
                        <Box>
                          <Box
                            display="inline-block"
                            className="profile-links-value"
                          >
                            {link?.label}
                          </Box>
                          <Box marginLeft="8px" display="inline-block">
                            <svg
                              width="19"
                              height="19"
                              viewBox="0 0 19 19"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M14.5382 12.2986L14.5382 4.46152M14.5382 4.46152L6.70108 4.46152M14.5382 4.46152L4.46191 14.5378"
                                stroke="#2563EB"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </Box>
                        </Box>
                      </Link>
                    </Box>
                  ) : null
                )}
              </Box>
            )}
          </Box>
        )}
        {commentsEnabled && mobileTab === 'comments' && comments.length > 0 && (
          <Box padding="0 0 16px 0">
            {isUserLoggedIn && !canEdit && (
              <CommentInput
                onSubmit={handleSubmitComment}
                loading={commentsLoading}
                isLoggedIn={isUserLoggedIn}
              />
            )}
            {commentsError && (
              <Box marginBottom="8px" color={Colors.ORANGE}>
                {commentsError}
              </Box>
            )}
            <CommentCarousel
              comments={comments}
              loading={commentsLoading}
              lastUpdate={lastUpdate}
              onRefresh={refreshComments}
              currentPage={currentPage}
              totalPages={totalPages}
              hasMore={hasMore}
              totalCount={totalCount}
              onNextPage={nextPage}
              onPrevPage={prevPage}
              onGoToPage={goToPage}
            />
          </Box>
        )}
      </Box>

      {commentsEnabled && (
        <CommentModal
          isOpen={isCommentModalOpen}
          onClose={() => setIsCommentModalOpen(false)}
          onSubmit={handleModalSubmit}
          profileName={name}
        />
      )}
      
    </Box>
  );
};

export default ProfileTile;
